import React from 'react';
import {
  COMPLAINT_TEMPLATES, RX_TEMPLATES, DRUG_SUGGESTIONS, FOOD_OPTIONS,
  ICD10_COMMON, LAB_PANELS, LAB_TESTS_FLAT, EXAM_SYSTEMS, PMH_OPTIONS, emptyRxLine,
} from '../../config/emrCatalog';
import { VITAL_FIELDS } from '../../config/emrSettings';
import { calcBmi, bmiCategory, vitalAbnormal, dispenseQty } from '../../utils/emrHelpers';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const VITAL_META = {
  bp: { label: 'BP (mmHg)', ph: '120/80' },
  pulse: { label: 'Pulse (/min)', ph: '72' },
  temp: { label: 'Temp (°F)', ph: '98.4' },
  spo2: { label: 'SpO₂ (%)', ph: '98' },
  rr: { label: 'RR (/min)', ph: '18' },
  weight: { label: 'Weight (kg)', ph: '70' },
  height: { label: 'Height (cm)', ph: '170' },
};

function Chip({ children, onClick, active }) {
  return (
    <button type="button" className={`emr-chip ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

export default function EmrTabPanel({
  tab, form, setForm, readOnly = false, inventory = [], labCatalog = [], genieMeds = [], emrSettings = {},
}) {
  const sectionTemplates = emrSettings.sectionTemplates || {};
  const medCfg = emrSettings.medSectionConfig || {};
  const vitalFields = (emrSettings.vitalsConfig?.fields?.length
    ? emrSettings.vitalsConfig.fields
    : VITAL_FIELDS.map((f) => f.id));
  const findInv = (line) => {
    if (line.itemId) return inventory.find((i) => i.itemId === line.itemId) || null;
    const q = (line.drug || '').toLowerCase();
    if (!q) return null;
    return inventory.find((i) => (i.name || '').toLowerCase() === q)
      || inventory.find((i) => q.includes((i.name || '').toLowerCase()) || (i.name || '').toLowerCase().includes(q))
      || null;
  };
  const setRxDrug = (idx, value) => {
    const exact = inventory.find((i) => (i.name || '').toLowerCase() === value.toLowerCase());
    setForm((f) => {
      const rxLines = [...f.rxLines];
      rxLines[idx] = { ...rxLines[idx], drug: value, itemId: exact?.itemId || '', generic: exact?.genericName || rxLines[idx].generic };
      return { ...f, rxLines };
    });
  };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setHpi = (k, v) => setForm((f) => ({ ...f, hpi: { ...f.hpi, [k]: v } }));
  const setHist = (k, v) => setForm((f) => ({ ...f, history: { ...f.history, [k]: v } }));
  const setVital = (k, v) => {
    setForm((f) => {
      const vitals = { ...f.vitals, [k]: v };
      if (k === 'weight' || k === 'height') vitals.bmi = calcBmi(vitals.weight, vitals.height);
      return { ...f, vitals };
    });
  };
  const setExam = (id, patch) =>
    setForm((f) => ({ ...f, examination: { ...f.examination, [id]: { ...f.examination[id], ...patch } } }));
  const setAssess = (k, v) => setForm((f) => ({ ...f, assessment: { ...f.assessment, [k]: v } }));
  const setPlan = (k, v) => setForm((f) => ({ ...f, plan: { ...f.plan, [k]: v } }));
  const setSoap = (k, v) => setForm((f) => ({ ...f, soap: { ...f.soap, [k]: v } }));

  const setRx = (idx, field, value) => {
    setForm((f) => {
      const rxLines = [...f.rxLines];
      rxLines[idx] = { ...rxLines[idx], [field]: value };
      return { ...f, rxLines };
    });
  };

  const togglePmh = (item) => {
    setForm((f) => {
      const pmh = f.history.pmh || [];
      const next = pmh.includes(item) ? pmh.filter((x) => x !== item) : [...pmh, item];
      return { ...f, history: { ...f.history, pmh: next } };
    });
  };

  const toggleLab = (test) => {
    setForm((f) => {
      const labOrders = f.labOrders || [];
      const next = labOrders.includes(test) ? labOrders.filter((t) => t !== test) : [...labOrders, test];
      return { ...f, labOrders: next };
    });
  };

  const wrap = (panel) => (
    <fieldset disabled={readOnly} className="emr-readonly-wrap">
      {panel}
    </fieldset>
  );

  if (tab === 'consult') {
    return wrap(
      <div className="emr-panel">
        <div className="emr-template-row">
          <span className="hint">Quick templates:</span>
          {COMPLAINT_TEMPLATES.map((t) => (
            <Chip key={t.label} onClick={() => set({ complaint: t.text })}>{t.label}</Chip>
          ))}
          {(sectionTemplates.symptoms || []).map((t) => (
            <Chip key={t} onClick={() => set({ complaint: t })}>{t}</Chip>
          ))}
        </div>
        <div className="field full">
          <label>Chief complaint</label>
          <textarea className="input" rows={3} value={form.complaint} onChange={(e) => set({ complaint: e.target.value })} placeholder="Patient presents with…" />
        </div>
        <h4 className="section-label">History of present illness (HPI)</h4>
        <div className="form-grid">
          <div className="field"><label>Onset</label><input className="input" value={form.hpi.onset} onChange={(e) => setHpi('onset', e.target.value)} placeholder="Sudden / gradual" /></div>
          <div className="field"><label>Duration</label><input className="input" value={form.hpi.duration} onChange={(e) => setHpi('duration', e.target.value)} placeholder="3 days" /></div>
          <div className="field"><label>Severity</label><input className="input" value={form.hpi.severity} onChange={(e) => setHpi('severity', e.target.value)} placeholder="Mild / moderate / severe" /></div>
          <div className="field"><label>Location</label><input className="input" value={form.hpi.location} onChange={(e) => setHpi('location', e.target.value)} /></div>
          <div className="field"><label>Aggravating factors</label><input className="input" value={form.hpi.aggravating} onChange={(e) => setHpi('aggravating', e.target.value)} /></div>
          <div className="field"><label>Relieving factors</label><input className="input" value={form.hpi.relieving} onChange={(e) => setHpi('relieving', e.target.value)} /></div>
          <div className="field full"><label>Associated symptoms</label><input className="input" value={form.hpi.associated} onChange={(e) => setHpi('associated', e.target.value)} /></div>
        </div>
      </div>
    );
  }

  if (tab === 'history') {
    return wrap(
      <div className="emr-panel">
        <div className="field full emr-allergy-field">
          <label>Drug / food allergies (critical)</label>
          <input className="input" value={form.history.allergies} onChange={(e) => setHist('allergies', e.target.value)} placeholder="e.g. Penicillin — rash; None known" />
        </div>
        <div className="field full">
          <label>Past medical history</label>
          <div className="emr-chip-grid">
            {PMH_OPTIONS.map((o) => (
              <Chip key={o} active={(form.history.pmh || []).includes(o)} onClick={() => togglePmh(o)}>{o}</Chip>
            ))}
          </div>
        </div>
        <div className="form-grid">
          <div className="field full"><label>Surgical history</label><input className="input" value={form.history.surgical} onChange={(e) => setHist('surgical', e.target.value)} /></div>
          <div className="field full"><label>Family history</label><input className="input" value={form.history.family} onChange={(e) => setHist('family', e.target.value)} /></div>
          <div className="field full"><label>Social history (smoking, alcohol, occupation)</label><input className="input" value={form.history.social} onChange={(e) => setHist('social', e.target.value)} /></div>
          <div className="field full"><label>Current medications</label><textarea className="input" rows={2} value={form.history.currentMeds} onChange={(e) => setHist('currentMeds', e.target.value)} /></div>
        </div>
      </div>
    );
  }

  if (tab === 'vitals') {
    const bmi = form.vitals.bmi || calcBmi(form.vitals.weight, form.vitals.height);
    return wrap(
      <div className="emr-panel">
        <h4 className="section-label">Vitals</h4>
        <div className="emr-vitals-grid">
          {vitalFields.filter((k) => VITAL_META[k]).map((k) => {
            const { label, ph } = VITAL_META[k];
            return (
            <div key={k} className={`emr-vital-box ${vitalAbnormal(k, form.vitals[k]) ? 'abnormal' : ''}`}>
              <label>{label}</label>
              <input className="input" placeholder={ph} value={form.vitals[k] || ''} onChange={(e) => setVital(k, e.target.value)} />
            </div>
            );
          })}
          <div className="emr-vital-box bmi-box">
            <label>BMI</label>
            <div className="bmi-value">{bmi || '—'}</div>
            <span className="hint">{bmiCategory(bmi)}</span>
          </div>
        </div>
        <h4 className="section-label mt">Physical examination</h4>
        <div className="emr-exam-list">
          {EXAM_SYSTEMS.map((sys) => (
            <div key={sys.id} className="emr-exam-row">
              <div className="emr-exam-head">
                <strong>{sys.label}</strong>
                <label className="emr-check">
                  <input
                    type="checkbox"
                    checked={form.examination[sys.id]?.abnormal}
                    onChange={(e) => setExam(sys.id, { abnormal: e.target.checked })}
                  />
                  Abnormal
                </label>
              </div>
              <textarea
                className="input"
                rows={2}
                value={form.examination[sys.id]?.finding || ''}
                onChange={(e) => setExam(sys.id, { finding: e.target.value })}
                placeholder={sys.normals}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 'assessment') {
    return wrap(
      <div className="emr-panel">
        <div className="form-grid">
          <div className="field full">
            <label>Primary diagnosis</label>
            <input className="input" value={form.assessment.primary} onChange={(e) => setAssess('primary', e.target.value)} />
          </div>
          {(sectionTemplates.diagnosis || []).length > 0 && (
            <div className="emr-template-row full">
              {(sectionTemplates.diagnosis || []).map((t) => (
                <Chip key={t} onClick={() => setAssess('primary', t)}>{t}</Chip>
              ))}
            </div>
          )}
          <div className="field full">
            <label>ICD-10</label>
            <input className="input" list="icd-list" value={form.assessment.icd10} onChange={(e) => setAssess('icd10', e.target.value)} placeholder="Search code…" />
            <datalist id="icd-list">
              {ICD10_COMMON.map((i) => <option key={i.code} value={i.code}>{i.label}</option>)}
            </datalist>
          </div>
          <div className="emr-template-row full">
            {ICD10_COMMON.slice(0, 8).map((i) => (
              <Chip
                key={i.code}
                onClick={() => setForm((f) => ({
                  ...f,
                  assessment: { ...f.assessment, icd10: i.code, primary: f.assessment.primary || i.label },
                }))}
              >
                {i.code}
              </Chip>
            ))}
          </div>
          <div className="field full"><label>Differential diagnosis</label><input className="input" value={form.assessment.differential} onChange={(e) => setAssess('differential', e.target.value)} /></div>
          <div className="field full"><label>Clinical impression</label><textarea className="input" rows={3} value={form.assessment.clinicalImpression} onChange={(e) => setAssess('clinicalImpression', e.target.value)} /></div>
        </div>
      </div>
    );
  }

  if (tab === 'rx') {
    const quickMeds = [...new Set([...(genieMeds || []), ...DRUG_SUGGESTIONS])];
    return wrap(
      <div className="emr-panel">
        {genieMeds?.length > 0 && (
          <div className="emr-template-row">
            <span className="hint">Your quick medicines:</span>
            {genieMeds.map((d) => (
              <Chip
                key={d}
                onClick={() => setForm((f) => ({
                  ...f,
                  rxLines: [...f.rxLines.filter((l) => l.drug), { ...emptyRxLine(), drug: d }],
                }))}
              >
                {d}
              </Chip>
            ))}
          </div>
        )}
        <div className="emr-template-row">
          <span className="hint">Rx templates:</span>
          {Object.keys(RX_TEMPLATES).map((k) => (
            <Chip key={k} onClick={() => set({ rxLines: RX_TEMPLATES[k].map((r) => ({ ...r })) })}>{k.toUpperCase()}</Chip>
          ))}
        </div>
        <div className="emr-rx-table">
          <div className="emr-rx-head">
            <span>Medicine</span><span>Dose</span><span>M · A · N</span><span>Food</span><span>Duration</span><span>Instructions</span><span />
          </div>
          {form.rxLines.map((rx, idx) => {
            const inv = findInv(rx);
            const qty = dispenseQty(rx);
            return (
              <div key={idx} className="emr-rx-row">
                <div className="emr-rx-drug">
                  <input className="input" list="drug-list" value={rx.drug} onChange={(e) => setRxDrug(idx, e.target.value)} placeholder="Drug name" />
                  {medCfg.showGeneric !== false && rx.generic && (
                    <span className="hint" style={{ fontSize: 11 }}>{rx.generic}</span>
                  )}
                  {rx.drug ? (
                    inv
                      ? <span className="emr-stock-hint ok">In stock: {inv.stockQty} · MRP {inr(inv.mrp)}{qty ? ` · need ${qty}` : ''}</span>
                      : <span className="emr-stock-hint warn">Not in pharmacy inventory</span>
                  ) : null}
                </div>
                <input className="input" value={rx.dose} onChange={(e) => setRx(idx, 'dose', e.target.value)} placeholder="1 tab" />
                <div className="emr-man">
                  <input className="input" type="number" min={0} value={rx.m ?? ''} onChange={(e) => setRx(idx, 'm', e.target.value === '' ? '' : Number(e.target.value))} aria-label="Morning" />
                  <input className="input" type="number" min={0} value={rx.a ?? ''} onChange={(e) => setRx(idx, 'a', e.target.value === '' ? '' : Number(e.target.value))} aria-label="Afternoon" />
                  <input className="input" type="number" min={0} value={rx.n ?? ''} onChange={(e) => setRx(idx, 'n', e.target.value === '' ? '' : Number(e.target.value))} aria-label="Night" />
                </div>
                <select className="input" value={rx.food || 'After food'} onChange={(e) => setRx(idx, 'food', e.target.value)}>
                  {FOOD_OPTIONS.map((f) => <option key={f}>{f}</option>)}
                </select>
                <input className="input" value={rx.duration} onChange={(e) => setRx(idx, 'duration', e.target.value)} placeholder={medCfg.defaultDuration || '5 days'} />
                {medCfg.showInstructions !== false && (
                <input className="input" value={rx.instructions} onChange={(e) => setRx(idx, 'instructions', e.target.value)} placeholder="Notes" />
                )}
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setForm((f) => ({ ...f, rxLines: f.rxLines.filter((_, i) => i !== idx) }))}>×</button>
              </div>
            );
          })}
          <datalist id="drug-list">
            {inventory.map((i) => <option key={i.itemId} value={i.name} />)}
            {quickMeds.map((d) => <option key={d} value={d} />)}
          </datalist>
        </div>
        <button type="button" className="btn btn-secondary btn-sm mt" onClick={() => setForm((f) => ({
          ...f,
          rxLines: [...f.rxLines, { ...emptyRxLine(), duration: medCfg.defaultDuration || '5 days' }],
        }))}>+ Add medicine</button>
        <p className="hint mt">M · A · N = doses morning / afternoon / night. Matched medicines will be priced &amp; stock-checked when sent to pharmacy.</p>
      </div>
    );
  }

  if (tab === 'labs') {
    const labEstimate = (form.labOrders || []).reduce((s, name) => {
      const t = labCatalog.find((c) => c.name === name);
      return s + Number(t?.rate || 0);
    }, 0);
    return wrap(
      <div className="emr-panel">
        {labCatalog.length > 0 && (
          <div className="emr-lab-panel">
            <h4 className="section-label">From your lab catalog (priced)</h4>
            <div className="emr-chip-grid">
              {labCatalog.map((t) => (
                <Chip key={t.testId} active={(form.labOrders || []).includes(t.name)} onClick={() => toggleLab(t.name)}>
                  {t.name} · {inr(t.rate)}
                </Chip>
              ))}
            </div>
          </div>
        )}
        {Object.entries(LAB_PANELS).map(([panel, tests]) => (
          <div key={panel} className="emr-lab-panel">
            <h4 className="section-label">{panel.charAt(0).toUpperCase() + panel.slice(1)} panel</h4>
            <div className="emr-chip-grid">
              {tests.map((t) => (
                <Chip key={t} active={(form.labOrders || []).includes(t)} onClick={() => toggleLab(t)}>{t}</Chip>
              ))}
            </div>
          </div>
        ))}
        <div className="field full mt">
          <label>Additional tests / imaging</label>
          <input className="input" list="lab-flat" value={form.labNotes} onChange={(e) => set({ labNotes: e.target.value })} placeholder="Custom orders…" />
          <datalist id="lab-flat">{LAB_TESTS_FLAT.map((t) => <option key={t} value={t} />)}</datalist>
        </div>
        {(form.labOrders || []).length > 0 && (
          <p className="hint mt">Selected: {(form.labOrders || []).join(', ')}{labEstimate > 0 ? ` · est. ${inr(labEstimate)}` : ''}</p>
        )}
      </div>
    );
  }

  if (tab === 'plan') {
    return wrap(
      <div className="emr-panel">
        {(sectionTemplates.advice || []).length > 0 && (
          <div className="emr-template-row mb">
            {(sectionTemplates.advice || []).map((t) => (
              <Chip key={t} onClick={() => setPlan('advice', [form.plan?.advice, t].filter(Boolean).join('. '))}>{t}</Chip>
            ))}
          </div>
        )}
        <div className="form-grid">
          <div className="field full"><label>Patient advice</label><textarea className="input" rows={3} value={form.plan.advice} onChange={(e) => setPlan('advice', e.target.value)} placeholder="Rest, fluids, avoid cold food…" /></div>
          <div className="field"><label>Follow-up</label><input className="input" value={form.plan.followUp} onChange={(e) => setPlan('followUp', e.target.value)} placeholder="7 days / SOS" /></div>
          <div className="field"><label>Referral</label><input className="input" value={form.plan.referral} onChange={(e) => setPlan('referral', e.target.value)} placeholder="Specialist if needed" /></div>
          <div className="field"><label>Diet</label><input className="input" value={form.plan.diet} onChange={(e) => setPlan('diet', e.target.value)} /></div>
          <div className="field"><label>Activity</label><input className="input" value={form.plan.activity} onChange={(e) => setPlan('activity', e.target.value)} /></div>
          <div className="field full"><label>Warning signs (return if)</label><input className="input" value={form.plan.warningSigns} onChange={(e) => setPlan('warningSigns', e.target.value)} placeholder="High fever, breathlessness, chest pain…" /></div>
        </div>
      </div>
    );
  }

  if (tab === 'notes') {
    return wrap(
      <div className="emr-panel">
        <p className="hint mb">SOAP notes — use &quot;Auto-fill SOAP&quot; in the action bar to generate from your entries.</p>
        <div className="form-grid">
          <div className="field full"><label>Subjective</label><textarea className="input" rows={3} value={form.soap.subjective} onChange={(e) => setSoap('subjective', e.target.value)} /></div>
          <div className="field full"><label>Objective</label><textarea className="input" rows={3} value={form.soap.objective} onChange={(e) => setSoap('objective', e.target.value)} /></div>
          <div className="field full"><label>Assessment</label><textarea className="input" rows={2} value={form.soap.assessment} onChange={(e) => setSoap('assessment', e.target.value)} /></div>
          <div className="field full"><label>Plan</label><textarea className="input" rows={3} value={form.soap.plan} onChange={(e) => setSoap('plan', e.target.value)} /></div>
          <div className="field full"><label>Additional notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
        </div>
      </div>
    );
  }

  return null;
}
